import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from '../../../../components/Common/Modal';
import {
  CloseIcon,
  ImageContainerLeftSection,
  ImageLeftSection,
  ImageViewContainer,
  ImageViewLeftSection,
  ImageViewRightSection,
  ImageViewRightSectionImage,
  ImageViewRightSectionNext,
  ImageViewRightSectionPrev,
} from './ImageDescriptionModal.styles';
import { CloseIconGrey } from '@/assets/svgExports/CloseIconGrey';
import { ImageModalBackArrow } from '@/assets/svgExports/ImageModalBackArrow';
import { ImageModalLeftArrow } from '@/assets/svgExports/ImageModalLeftArrow';
import { LeftArrowImageModalMobile } from '@/assets/svgExports/LeftArrowImageModalMobile';
import { PlayIcon } from '@/assets/svgExports/PlayIcon';
import { useState } from 'react';
import { deviceWidth } from '@/utils/deviceWidth';
import { getImageThumbnailUrl } from '@/utils/getImageThumbnailUrl';
import { MAX_MOBILE_WIDTH, MEDIA_TYPES } from '@/utils/constants';
import useIsComponentMounted from '@/utils/useIsComponentMounted';
import useSetImagesDescriptionModal from '../../../../hooks/useSetImagesDescriptionModal';
import YouTubeViewer from '../ImageDescriptionModal/YouTubeViewer';

const SCROLL_ACTION = {
  NEXT: 'NEXT',
  PREV: 'PREV',
};

const getImageDimensionsAccordingToAspectRatio = (
  isMobile,
  aspectRatio,
  isCustomWebsite
) => {
  if (isMobile) {
    const imageDimensionsObj = {
      height: '360px',
      width: '100vw',
    };
    if (aspectRatio > 100 && isCustomWebsite) {
      imageDimensionsObj.width = '100vw';
      imageDimensionsObj.height = '202px';
    }
    if (aspectRatio < 100 && isCustomWebsite) {
      imageDimensionsObj.width = '202px';
      imageDimensionsObj.height = '360px';
    }
    return imageDimensionsObj;
  }
  const imageDimensionsObj = {
    height: '600px',
    width: '600px',
  };
  if (aspectRatio > 100 && isCustomWebsite) {
    imageDimensionsObj.width = '600px';
    imageDimensionsObj.height = '338px';
  }
  if (aspectRatio < 100 && isCustomWebsite) {
    imageDimensionsObj.width = '338px';
    imageDimensionsObj.height = '600px';
  }
  return imageDimensionsObj;
};

const getThumbnailImageDimensionsAccordingToAspectRatio = (
  aspectRatio,
  isCustomWebsite
) => {
  const imageDimensionsObj = {
    height: '48px',
    width: '48px',
  };
  if (aspectRatio > 100 && isCustomWebsite) {
    imageDimensionsObj.width = '48px';
    imageDimensionsObj.height = '27px';
  }
  if (aspectRatio < 100 && isCustomWebsite) {
    imageDimensionsObj.width = '27px';
    imageDimensionsObj.height = '48px';
  }
  return imageDimensionsObj;
};

const getPlayIconPosition = (aspectRatio, isCustomWebsite) => {
  if (isCustomWebsite && aspectRatio > 100) return '4px 0px 0px 12px';
  if (isCustomWebsite && aspectRatio < 100) return '12px 0px 0px 2px';
  return '12px 0px 0px 12px';
};

function ImageDescriptionModal(props) {
  const {
    handleShowImageDescriptionModal,
    showImageDescModal,
    productData,
    activeIdx,
    mainImageIndexFromPdp,
  } = props;
  const isMobile = deviceWidth <= MAX_MOBILE_WIDTH;
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const selectedPanelToView = useRef();
  const isComponentMounted = useIsComponentMounted();
  const { theme } = useSelector((state) => ({
    theme: state.storeReducer.store.theme,
  }));

  useEffect(() => {
    selectedPanelToView.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }, [mainImageIndex]);

  useEffect(() => {
    setMainImageIndex(mainImageIndexFromPdp);
  }, [mainImageIndexFromPdp, activeIdx]);

  const imageDimensions = getImageDimensionsAccordingToAspectRatio(
    isMobile,
    theme?.product_aspect_ratio,
    theme?.custom_theme_flag
  );
  const thumbnailImageDimensions = getThumbnailImageDimensionsAccordingToAspectRatio(
    theme?.product_aspect_ratio,
    theme?.custom_theme_flag
  );
  const playIconPosition = getPlayIconPosition(
    theme?.product_aspect_ratio,
    theme?.custom_theme_flag
  );

  const images = useSetImagesDescriptionModal({
    productData,
    activeIdx,
    showImageDescModal,
  });

  function handleNextPrevIcon(clickType) {
    if (clickType === SCROLL_ACTION.NEXT) {
      if (mainImageIndex < images?.length - 1) {
        setMainImageIndex((prevIndex) => prevIndex + 1);
      } else {
        setMainImageIndex(0);
      }
    } else {
      if (mainImageIndex > 0) {
        setMainImageIndex((prevIndex) => prevIndex - 1);
      } else {
        setMainImageIndex(images?.length - 1);
      }
    }
  }

  function handleLeftPanelImageClick(index) {
    setMainImageIndex(index);
  }

  return (
    <Modal
      visible={showImageDescModal}
      className="bottom-modal imageDescriptionModal"
      animation={'slideUp'}
      height={isMobile ? '100vh' : 200}
      onClose={handleShowImageDescriptionModal}
    >
      <ImageViewContainer>
        <ImageViewLeftSection>
          {images.map((itemImage, index) => {
            return (
              <ImageContainerLeftSection
                ref={isMobile && index === mainImageIndex ? selectedPanelToView : null}
                thumbnailImageDimensions={thumbnailImageDimensions}
                key={`Left-section-panel-${itemImage.thumbnail_url}`}
                borderColor={index === mainImageIndex ? true : false}
                onClick={() => {
                  handleLeftPanelImageClick(index);
                }}
              >
                <ImageLeftSection
                  thumbnailImageDimensions={thumbnailImageDimensions}
                  src={getImageThumbnailUrl(itemImage, false)}
                />
                {itemImage?.media_type === MEDIA_TYPES.VIDEO && (
                  <div className="absolute" style={{ margin: playIconPosition }}>
                    <PlayIcon />
                  </div>
                )}
              </ImageContainerLeftSection>
            );
          })}
        </ImageViewLeftSection>
        <ImageViewRightSection>
          <ImageViewRightSectionPrev
            onClick={() => {
              handleNextPrevIcon(SCROLL_ACTION.PREV);
            }}
          >
            {isMobile ? <LeftArrowImageModalMobile /> : <ImageModalLeftArrow />}
          </ImageViewRightSectionPrev>
          {images[mainImageIndex]?.media_type === MEDIA_TYPES.VIDEO ? (
            showImageDescModal && (
              <YouTubeViewer linkUrl={images[mainImageIndex]?.image_url} />
            )
          ) : (
            <ImageViewRightSectionImage
              src={images[mainImageIndex]?.image_url}
              imageDimensions={imageDimensions}
              isCustomWebsite={theme?.custom_theme_flag}
            />
          )}
          <ImageViewRightSectionNext
            onClick={() => {
              handleNextPrevIcon(SCROLL_ACTION.NEXT);
            }}
          >
            {isMobile ? <LeftArrowImageModalMobile /> : <ImageModalLeftArrow />}
          </ImageViewRightSectionNext>
        </ImageViewRightSection>
        <CloseIcon onClick={handleShowImageDescriptionModal}>
          {isMobile && isComponentMounted ? <ImageModalBackArrow /> : <CloseIconGrey />}
        </CloseIcon>
      </ImageViewContainer>
    </Modal>
  );
}

export default ImageDescriptionModal;
